import { point } from '@turf/turf';
import { type LayerProps, Layer as ReactLayer, Source, type SourceProps } from 'react-map-gl/mapbox';
import {
  getFontAwesomeIcon,
  getFontAwesomeStyleCode,
  getPaintColorsV2,
  getSourceId,
  mapProp
} from 'sonata-static-storage/lib/helpers/map.helpers';

import { IconLayer } from '../IconLayer/IconLayer';

import type { FeatureCollection, Point } from 'geojson';
import type { ExtendedLayer } from 'sonata-static-storage/lib/common/common.types';
import type { LayerMapData } from 'sonata-static-storage/lib/common/itemsGrouping.types';
import type { MapViewerFilters, MapViewerSelectedItems } from '../mapViewer.types';

type Props = {
  filters: MapViewerFilters;
  layerMapData: LayerMapData[];
  layer: ExtendedLayer;
  selectedItems: MapViewerSelectedItems;
};

const TOTAL_WEIGHT = 100;

export function PointLayer({ layer, layerMapData, selectedItems }: Props): JSX.Element {
  const { config, poiFilters } = layer;
  const type: Record<string, string> = {
    locationsLayer: 'location',
    screensLayer: 'venue',
    venuesLayer: 'venue'
  };

  const id = config._id || 'point_layer';
  const sourceId = `${id}_${type[config.type]}`;
  const hasCustomIcons = config.customIcons && config.customIcons.length > 1;
  const paintData = getPaintColorsV2(config.color, config.indexLimits, selectedItems);

  const getIconCases = (): string | unknown[] => {
    const { customIcons, icon } = config;
    if (!customIcons || customIcons.length === 0) {
      return `${id}_${icon}`;
    }
    const casesList: unknown[] = ['case'];
    customIcons.forEach((customIcon: string) => {
      casesList.push(['==', mapProp('icon', 'noIcon'), customIcon]);
      casesList.push(`${id}_${customIcon}`);
    });
    casesList.push(`${id}_${icon}`);
    return casesList;
  };

  const layerOptions = {
    id: `layer_${id}`,
    paint: {
      'circle-color': paintData,
      'circle-opacity': 1,
      'circle-radius': ['interpolate', ['linear'], ['zoom'], 10, 4, 12, 6, 14, 10, 16, 14]
    },
    type: 'circle'
  };

  const layerCustomIconsOptions = {
    id: `layer_${id}_icon`,
    layout: {
      'icon-image': getIconCases(),
      'icon-size': ['interpolate', ['linear'], ['zoom'], 11, 0, 12, 0.4, 14, 0.6, 16, 0.8]
    },
    paint: {},
    type: 'symbol'
  };

  const { audience, icon = '', type: layerType } = config;
  const fontAwesomeIcon = getFontAwesomeIcon(icon, layerType);
  const data: FeatureCollection = {
    features:
      layerMapData?.map((item: LayerMapData) => {
        return point((item.polygon as Point).coordinates, {
          elementId: item.elementId,
          icon: fontAwesomeIcon,
          iconStyleCode: getFontAwesomeStyleCode(icon),
          name: item.name,
          source: getSourceId(item.elementId, poiFilters),
          weight: audience ? item.w : TOTAL_WEIGHT
        });
      }) || [],
    type: 'FeatureCollection'
  };

  return (
    <Source key={`${sourceId}_${id}`} {...({ data, generateId: true, id: sourceId, type: 'geojson' } as SourceProps)}>
      <ReactLayer key={`layer_${id}`} {...(layerOptions as LayerProps)} />
      {hasCustomIcons ? (
        <ReactLayer key={`layer_${id}_icon`} {...(layerCustomIconsOptions as LayerProps)} />
      ) : (
        ['far', 'fal', 'fak', 'fas'].map(styleCode => (
          <IconLayer id={id} key={`iconLayer_${styleCode}`} source={sourceId} styleCode={styleCode} />
        ))
      )}
    </Source>
  );
}
